import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, CreditCard, Info, Copy, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";

interface StripePriceConfig {
  grade: number;
  plan: "monthly" | "yearly";
  priceId: string;
  amount: number;
  currency: string;
  description: string;
}

interface StripePricingResponse {
  pricing: StripePriceConfig[];
  priceMap: Record<string, Record<string, string>>;
  instructions: string[];
  lastUpdated: string;
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    toast({ title: "Copied to clipboard" });
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Button variant="ghost" size="icon" onClick={handleCopy} className="flex-shrink-0">
      {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
    </Button>
  );
}

export default function AdminStripePricing() {
  const { data, isLoading, error } = useQuery<StripePricingResponse>({
    queryKey: ["/api/admin/stripe-pricing"],
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <p className="text-sm text-muted-foreground">Failed to load Stripe pricing configuration.</p>
        </CardContent>
      </Card>
    );
  }

  const monthlyPrices = data.pricing.filter(p => p.plan === "monthly");
  const yearlyPrices = data.pricing.filter(p => p.plan === "yearly");

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Stripe Price Configuration</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Official Stripe Price IDs used in the signup flow. Last updated: {data.lastUpdated}
        </p>
      </div>

      <Card className="border-amber-200 dark:border-amber-900/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Info className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
            Developer Instructions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2">
            {data.instructions.map((instruction, i) => (
              <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                <Badge variant="outline" className="text-[10px] flex-shrink-0 mt-0.5">{i + 1}</Badge>
                <span>{instruction}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <CreditCard className="h-4 w-4 flex-shrink-0" />
              Monthly Subscriptions
            </CardTitle>
            <p className="text-xs text-muted-foreground">JMD $2,500/month</p>
          </CardHeader>
          <CardContent className="space-y-3">
            {monthlyPrices.map((price) => (
              <div key={price.priceId} className="flex items-center justify-between gap-2 p-3 rounded-md bg-muted">
                <div className="min-w-0">
                  <p className="text-sm font-medium">{price.description}</p>
                  <p className="text-xs text-muted-foreground font-mono truncate">{price.priceId}</p>
                </div>
                <CopyButton text={price.priceId} />
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <CreditCard className="h-4 w-4 flex-shrink-0" />
              Annual Subscriptions
            </CardTitle>
            <p className="text-xs text-muted-foreground">JMD $10,000/year</p>
          </CardHeader>
          <CardContent className="space-y-3">
            {yearlyPrices.map((price) => (
              <div key={price.priceId} className="flex items-center justify-between gap-2 p-3 rounded-md bg-muted">
                <div className="min-w-0">
                  <p className="text-sm font-medium">{price.description}</p>
                  <p className="text-xs text-muted-foreground font-mono truncate">{price.priceId}</p>
                </div>
                <CopyButton text={price.priceId} />
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">Price ID Lookup Map</CardTitle>
          <p className="text-xs text-muted-foreground">Used internally by the subscription creation endpoint</p>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 text-muted-foreground font-medium">Grade</th>
                  <th className="text-left py-2 text-muted-foreground font-medium">Monthly Price ID</th>
                  <th className="text-left py-2 text-muted-foreground font-medium">Annual Price ID</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(data.priceMap).map(([grade, plans]) => (
                  <tr key={grade} className="border-b last:border-b-0">
                    <td className="py-2.5">
                      <Badge variant="secondary">Grade {grade}</Badge>
                    </td>
                    <td className="py-2.5 font-mono text-xs text-muted-foreground">{plans.monthly}</td>
                    <td className="py-2.5 font-mono text-xs text-muted-foreground">{plans.yearly}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
